import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, useStripe, useElements, PaymentElement, ExpressCheckoutElement } from '@stripe/react-stripe-js';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase';
import stripeConfig from '../../config/stripe';
import './PaymentModal.css';

// Initialize Stripe with lazy loading
const stripeKey = stripeConfig.publishableKey;
let stripePromise = null;

const getStripePromise = () => {
  if (!stripePromise) {
    stripePromise = loadStripe(stripeKey).catch(error => {
      console.error('Failed to load Stripe.js:', error);
      // Return a rejected promise to handle gracefully
      return Promise.reject(error);
    });
  }
  return stripePromise;
};

// Helper for Card Icons
const CardIcon = ({ brand }) => {
  const brandLower = brand?.toLowerCase();
  
  if (brandLower === 'visa') {
    return (
      <svg width="32" height="10" viewBox="0 0 32 10" fill="none">
        <path d="M11.2334 0.178223L7.44238 8.84717L6.96875 6.64331L6.97119 6.63403C6.39868 4.70825 5.56787 2.87183 4.29858 0.178223H0L4.7937 9.82178H9.00635L15.3521 0.178223H11.2334Z" fill="#1A1F71"/>
        <path d="M19.3496 0.178223L16.2905 9.82178H20.1472L22.6108 0.178223H19.3496Z" fill="#1A1F71"/>
        <path d="M29.5 0.178223C27.9109 0.178223 26.5415 0.94165 25.6882 2.12646C25.4385 2.12646 23.3647 2.12646 22.8464 2.12646V3.88281C23.3088 3.88281 24.3645 3.88281 25.1099 3.88281V2.83911C25.334 2.19653 26.115 1.7063 27.1401 1.7063C28.2742 1.7063 29.5852 2.05493 29.5852 3.6521V4.28101H27.5681C25.3582 4.28101 23.2117 5.09326 23.2117 7.0542C23.2117 8.76636 24.9658 9.82178 27.2795 9.82178C29.2319 9.82178 30.7302 9.06543 31.5 7.82861V9.82178H35V3.81152C35 1.50391 33.1558 0.178223 29.5 0.178223ZM31.2505 6.77979C31.2505 8.16748 29.627 8.35864 28.5303 8.35864C27.324 8.35864 26.7905 7.97314 26.7905 7.20264L26.7915 7.20166C26.7915 6.38623 27.4243 5.74121 28.9868 5.74121H31.2505V6.77979Z" fill="#1A1F71"/>
      </svg>
    );
  }
  
  if (brandLower === 'mastercard') {
    return (
      <svg width="32" height="20" viewBox="0 0 32 20" fill="none">
        <circle cx="10" cy="10" r="10" fill="#EB001B"/>
        <circle cx="22" cy="10" r="10" fill="#F79E1B" fillOpacity="0.8"/>
      </svg>
    );
  }
  
  return <span className="generic-card-icon">💳</span>;
};

const PaymentForm = ({ selectedPlan, onSuccess, onError, clientSecret, savedCards = [], selectedCardId, setSelectedCardId }) => {
  const stripe = useStripe();
  const elements = useElements();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Check if user has active subscription for upgrade detection
  const { hasActiveSubscription, subscription, hasUsedTrial } = useSubscription();

  const displayDescription = (hasUsedTrial && selectedPlan.description)
    ? selectedPlan.description.replace(/with \d+-days free trial/i, '').replace(/- \d+ DAYS FREE/i, '').trim()
    : selectedPlan.description;

  const handleSubmit = async (event) => {
    event && event.preventDefault();

    if (!stripe) return;

    setLoading(true);
    setError('');

    // Determine if we are confirming a SetupIntent (seti_ for trials) or PaymentIntent (pi_ for direct pay)
    const isSetupIntent = clientSecret?.startsWith('seti_');

    try {
      let confirmResult;

      if (selectedCardId === 'new') {
        // Use Elements if "new card" is selected
        if (!elements) return;
        
        if (isSetupIntent) {
          confirmResult = await stripe.confirmSetup({
            elements,
            confirmParams: {
              return_url: `${window.location.origin}/thank-you?success=true`,
            },
            redirect: 'if_required',
          });
        } else {
          confirmResult = await stripe.confirmPayment({
            elements,
            confirmParams: {
              return_url: `${window.location.origin}/thank-you?success=true`,
            },
            redirect: 'if_required',
          });
        }
      } else {
        // Use saved card ID directly
        if (isSetupIntent) {
          confirmResult = await stripe.confirmSetup({
            clientSecret,
            confirmParams: {
              payment_method: selectedCardId,
              return_url: `${window.location.origin}/thank-you?success=true`,
            },
            redirect: 'if_required',
          });
        } else {
          confirmResult = await stripe.confirmPayment({
            clientSecret,
            confirmParams: {
              payment_method: selectedCardId,
              return_url: `${window.location.origin}/thank-you?success=true`,
            },
            redirect: 'if_required',
          });
        }
      }

      if (confirmResult.error) {
        throw new Error(confirmResult.error.message);
      }

      // If we reach here and there was no redirect, confirmation was successful
      onSuccess && onSuccess();
    } catch (err) {
      console.error('Payment error:', err);
      const errorMessage = err.message || 'Payment failed. Please try again.';
      setError(errorMessage);
      onError && onError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="payment-form">
      {error && (
        <div className="payment-error">
          {error}
        </div>
      )}

      {/* Modern instant pay button (Google Pay, Apple Pay, Link) */}
      <div className="direct-payment-section">
        <h4 className="payment-section-title">One-click Payment</h4>
        <ExpressCheckoutElement 
          onConfirm={handleSubmit} 
          options={{
            buttonType: {
              googlePay: 'buy',
              applePay: 'buy'
            }
          }}
        />
        <div className="payment-divider">
          <span>OR MANAGE CARDS BELOW</span>
        </div>
      </div>

      <div className="payment-plan-info">
        <h3>{selectedPlan.name}</h3>
        <div className="payment-price">
          <span className="price-amount">{selectedPlan.price}</span>
          <span className="price-period">{selectedPlan.period}</span>
        </div>
        <p className="payment-description">{displayDescription}</p>
      </div>

      {/* Saved Cards List (Direct Card Option) */}
      {savedCards.length > 0 && (
        <div className="saved-cards-section">
          <h4 className="payment-section-title">Use a saved card</h4>
          <div className="saved-cards-list">
            {savedCards.map((card) => (
              <div 
                key={card.id} 
                className={`saved-card-item ${selectedCardId === card.id ? 'selected' : ''}`}
                onClick={() => setSelectedCardId(card.id)}
              >
                <div className="card-radio">
                  <div className="radio-inner"></div>
                </div>
                <div className="card-brand-icon">
                  <CardIcon brand={card.brand} />
                </div>
                <div className="card-details">
                  <span className="card-number">•••• •••• •••• {card.last4}</span>
                  <span className="card-expiry">Expires {card.exp_month}/{card.exp_year}</span>
                </div>
              </div>
            ))}
            
            <div 
              className={`saved-card-item ${selectedCardId === 'new' ? 'selected' : ''}`}
              onClick={() => setSelectedCardId('new')}
            >
              <div className="card-radio">
                <div className="radio-inner"></div>
              </div>
              <div className="card-brand-icon">
                <span className="add-card-icon">+</span>
              </div>
              <div className="card-details">
                <span className="card-number">Use another payment method</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Inline card entry - Only show if 'new' is selected or no saved cards */}
      <div className="payment-element-wrapper" style={{ display: selectedCardId === 'new' ? 'block' : 'none' }}>
        <h4 className="payment-section-title">Enter card details</h4>
        <PaymentElement />
      </div>

      <button
        type="submit"
        className="payment-submit"
        disabled={!stripe || loading}
        style={{ marginTop: '24px' }}
      >
        {loading ? 'Processing...' : (
          selectedCardId === 'new' 
            ? `Subscribe for ${selectedPlan.price}${selectedPlan.period}`
            : `Pay Now with Saved Card`
        )}
      </button>
    </form>
  );
};

const PaymentModal = ({ isOpen, onClose, selectedPlan, onSuccess, initialClientSecret, initialPaymentMethods }) => {
  const { currentUser } = useAuth();
  const { hasUsedTrial } = useSubscription();
  const [mounted, setMounted] = useState(false);
  const [stripeLoading, setStripeLoading] = useState(true);
  const [clientSecret, setClientSecret] = useState(initialClientSecret || '');
  const [savedCards, setSavedCards] = useState(initialPaymentMethods || []);
  const [selectedCardId, setSelectedCardId] = useState('new');
  const [intentError, setIntentError] = useState(null);

  useEffect(() => {
    setMounted(true);
    
    const fetchIntent = async () => {
      // If we already have a secret, don't fetch it again to avoid "Concurrent Access" error
      if (initialClientSecret) {
        setClientSecret(initialClientSecret);
        if (initialPaymentMethods?.length > 0) {
           setSavedCards(initialPaymentMethods);
           setSelectedCardId(initialPaymentMethods[0].id);
        }
        setStripeLoading(false);
        return;
      }

      if (isOpen && selectedPlan && currentUser) {
        setStripeLoading(true);
        setIntentError(null);
        try {
          const createSubscriptionIntent = httpsCallable(functions, 'createSubscriptionIntent');
          const result = await createSubscriptionIntent({
            priceId: selectedPlan.priceId,
            userId: currentUser.uid,
            userEmail: currentUser.email,
            trialDays: hasUsedTrial ? 0 : (selectedPlan.trialDays || 0),
            mode: stripeConfig.mode
          });

          if (result.data?.clientSecret) {
            setClientSecret(result.data.clientSecret);
            const fetchedCards = result.data.paymentMethods || [];
            setSavedCards(fetchedCards);
            
            // Auto-select the first card if available
            if (fetchedCards.length > 0) {
              setSelectedCardId(fetchedCards[0].id);
            } else {
              setSelectedCardId('new');
            }
          } else {
            setIntentError('Failed to initialize payment. Please try again.');
          }
        } catch (error) {
          console.error('Error fetching subscription intent:', error);
          // Map raw errors to friendly messages
          const rawMsg = error.message || '';
          let friendlyMsg = 'We encountered an issue setting up payment. Please try again.';
          if (rawMsg.includes('already has 500') || rawMsg.includes('resource-exhausted')) {
            friendlyMsg = 'We could not initialize your subscription at this time. Please try again or contact support.';
          } else if (rawMsg.includes('network') || rawMsg.includes('fetch')) {
            friendlyMsg = 'Network error. Please check your connection and try again.';
          }
          setIntentError(friendlyMsg);
        } finally {
          setStripeLoading(false);
        }
      }
    };

    fetchIntent();
  }, [isOpen, selectedPlan, currentUser, hasUsedTrial, initialClientSecret, initialPaymentMethods]);

  if (!isOpen || !mounted) return null;

  const handleSuccess = () => {
    onClose();
  };

  const handleError = (error) => {
    console.error('Payment error:', error);
  };

  const elementsOptions = {
    clientSecret,
    appearance: {
      theme: 'stripe',
      variables: {
        colorPrimary: '#155DFC',
        colorBackground: '#ffffff',
        colorText: '#1e293b',
        borderRadius: '8px',
      },
    },
    // Force Google Pay and Apple Pay to the top if they are available
    paymentMethodOrder: ['google_pay', 'apple_pay', 'card', 'link'],
  };

  return (
    <div className="payment-modal-overlay">
      <div className="payment-modal-container">
        <button className="payment-modal-close" onClick={onClose}>
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>

        <div className="payment-modal-content">
          <div className="payment-modal-header">
            <h2>Complete Your Subscription</h2>
            <p>You're just one step away from unlimited flight tracking!</p>
          </div>

          {stripeLoading ? (
            <div className="stripe-loading">
              <div className="loading-spinner"></div>
              <p>Preparing secure payment...</p>
            </div>
          ) : intentError ? (
            <div className="stripe-loading">
              <div className="payment-error-icon">❌</div>
              <p style={{ color: '#dc2626' }}>{intentError}</p>
              <button 
                onClick={() => window.location.reload()} 
                className="payment-submit" 
                style={{ marginTop: '20px' }}
              >
                Try Again
              </button>
            </div>
          ) : !clientSecret ? (
            <div className="stripe-loading">
              <p>No client secret. Please reload.</p>
            </div>
          ) : (
            <Elements stripe={getStripePromise()} options={elementsOptions}>
              <PaymentForm
                selectedPlan={selectedPlan}
                onSuccess={onSuccess || handleSuccess}
                onError={handleError}
                clientSecret={clientSecret}
                savedCards={savedCards}
                selectedCardId={selectedCardId}
                setSelectedCardId={setSelectedCardId}
              />
            </Elements>
          )}

          <div className="payment-modal-footer">
            <p className="payment-security">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Secure payment powered by Stripe
            </p>
            <p className="payment-terms">
              By subscribing, you agree to our <Link to="/terms-and-conditions">Terms of Service</Link> and <Link to="/privacy-policy">Privacy Policy</Link>.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentModal;